import { useQuery } from '@apollo/client';
import classNames from 'classnames';
import React, { useContext, useMemo, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useIsSidebarOpen } from '../app/layout/IsSidebarOpenContext';
import useAppSelector, { useAppDispatch } from '../app/redux/useAppSelector';
import useRouteHandle from '../app/useRouteHandle';
import Button from '../components/Button';
import IconButton from '../components/IconButton';
import SVGIcon from '../components/SVGIcon';
import { gql } from '../graphql/generated/gql';
import { PlanTier } from '../graphql/generated/graphql';
import searchIcon from '../icons/non-prefilled/16x16/search.svg';
import sidebarToggleIcon from '../icons/non-prefilled/16x16/sidebarToggle.svg';
import newSidebarToggleIcon from '../icons/non-prefilled/16x16/sidebarToggleNew.svg';
import NotificationCountBadge from '../notification/NotificationCountBadge';
import Presence from '../post/Presence';
import PostEditButton from '../post/topbar/PostEditButton';
import { UIActions } from '../ui/uiSlice';
import { isFeatureEnabled } from '../utils/features';
import { externalEditText } from '../utils/post/externalPost';
import {
  LayoutSize,
  RIGHT_PANEL_WIDTH,
  SIDEBAR_SHIFT_BREAKPOINT,
} from '../utils/responsive/Sizes';
import useIsLayoutSize from '../utils/responsive/useIsLayoutSize';
import useSlug from '../utils/router/useSlug';
import { openPricingModal } from '../utils/url/pricingPath';
import useShortcut from '../utils/useShortcut';
import styles from './InternalTopbar.module.scss';
import PostModeToggle from './PostModeToggle';
import PostStatus from './PostStatus';
import SearchBar, { useSearchBar } from './SearchBar';
import TopbarCreatePostButton from './TopbarCreatePostButton';
import TopbarUserButton from './TopbarUserButton';

const FULL_SEARCH_CLASSNAME = styles.fullSearch;

const InnerContext = React.createContext<{
  isShowingMobileFullSearch: boolean;
  setIsShowingMobileFullSearch: React.Dispatch<React.SetStateAction<boolean>>;
  internalTopbarRef: React.RefObject<HTMLDivElement>;
}>({
  isShowingMobileFullSearch: false,
  setIsShowingMobileFullSearch: () => {},
  internalTopbarRef: React.createRef(),
});

const UNREAD_NOTIFICATIONS_QUERY = gql(`
  query unreadNotifications {
    session: currentSession {
      organization {
        id
        unreadNotifications(first: 0) {
          totalCount
        }
      }
    }
  }
`);

const POST_QUERY = gql(`
  query InternalTopbarPost($id: ID!) {
    post(id: $id) {
      id
      externalPost {
        id
        editUrl
        platform
      }
      ...PostEditButton_post
    }
  }
`);

interface ContainerProps {
  children: React.ReactNode;
}

const Container = ({ children }: ContainerProps) => {
  const isSidebarOpen = useIsSidebarOpen();
  const internalTopbarRef = useRef<HTMLDivElement | null>(null);
  const [isShowingMobileFullSearch, setIsShowingMobileFullSearch] =
    useState(false);

  const value = useMemo(
    () => ({
      isShowingMobileFullSearch,
      setIsShowingMobileFullSearch,
      internalTopbarRef,
    }),
    [isShowingMobileFullSearch, setIsShowingMobileFullSearch],
  );

  return (
    <InnerContext.Provider value={value}>
      <header id="topbar" role="banner" aria-label="Topbar">
        <div
          ref={internalTopbarRef}
          className={classNames(styles.internalTopbar, {
            [FULL_SEARCH_CLASSNAME]: isShowingMobileFullSearch,
            [styles.sidebarOpen]: isSidebarOpen,
            [styles.newPostLayout]: isFeatureEnabled('new_post_layout'),
          })}
        >
          {children}
        </div>
      </header>
    </InnerContext.Provider>
  );
};

const SidebarToggle = () => {
  const dispatch = useAppDispatch();
  const isSidebarOpen = useIsSidebarOpen();
  const isRightPanelOpen = useAppSelector(
    (state) => state.ui.rightPanel.isOpen,
  );

  const { data: unreadNotificationData } = useQuery(
    UNREAD_NOTIFICATIONS_QUERY,
    {
      skip: isSidebarOpen,
    },
  );

  const unreadNotificationsCount =
    unreadNotificationData?.session?.organization.unreadNotifications
      ?.totalCount ?? 0;

  const { routeName } = useRouteHandle();

  return (
    <div className={styles.sidebarToggle}>
      <IconButton
        onClick={() => {
          const isHistory = ['post-history', 'comment-history'].includes(
            routeName,
          );

          if (
            isFeatureEnabled('new_post_layout') &&
            !isHistory &&
            !isSidebarOpen &&
            isRightPanelOpen &&
            window.innerWidth - RIGHT_PANEL_WIDTH < SIDEBAR_SHIFT_BREAKPOINT
          ) {
            dispatch(UIActions.closeRightPanel());
          }
          dispatch(UIActions.toggleSidebar());
        }}
        aria-label="Toggle toolbar"
      >
        <SVGIcon size="large">
          {isFeatureEnabled('new_post_layout')
            ? newSidebarToggleIcon
            : sidebarToggleIcon}
        </SVGIcon>
        {!isSidebarOpen && (
          <div className={styles.notificationButtonWrapper}>
            <NotificationCountBadge count={unreadNotificationsCount} />
          </div>
        )}
      </IconButton>
    </div>
  );
};

interface ContainerProps {
  children: React.ReactNode;
}

const SearchBarContainer = ({ children }: ContainerProps) => {
  const { isExpanded } = useSearchBar();

  return (
    <div
      className={classNames(styles.searchContainer, {
        [styles.isExpanded]: isExpanded,
      })}
    >
      {children}
    </div>
  );
};

const Search = () => {
  const { setIsShowingMobileFullSearch, internalTopbarRef } =
    useContext(InnerContext);
  const [isQuickJumpSearch, setIsQuickJumpSearch] = useState(false);
  const searchInputRef = useRef<{ focus(): void } | null>(null);

  const showAndFocusSearch = (quickJump = false) => {
    setIsShowingMobileFullSearch(true);
    setIsQuickJumpSearch(quickJump);

    // Manipulate classes directly to allow auto-focus on mobile Safari.
    internalTopbarRef.current?.classList.add(FULL_SEARCH_CLASSNAME);
    searchInputRef.current?.focus();
  };

  useShortcut('/', (e) => {
    e.preventDefault();
    showAndFocusSearch();
  });

  useShortcut('mod+k', (e) => {
    e.preventDefault();
    showAndFocusSearch(true);
  });

  return (
    <>
      <SearchBar.Root
        onExpandedChange={(expanded) => {
          if (!expanded) {
            setIsQuickJumpSearch(false);
          }
        }}
      >
        <SearchBarContainer>
          <div className={styles.searchInputContainer}>
            <SearchBar.Input
              ref={searchInputRef}
              quickJump={isQuickJumpSearch}
            />
          </div>
          <div className={styles.mobileSearchCancel}>
            <Button onClick={() => setIsShowingMobileFullSearch(false)}>
              Cancel
            </Button>
          </div>
        </SearchBarContainer>
      </SearchBar.Root>
      <div className={styles.mobileSearchButton}>
        <IconButton
          onClick={() => showAndFocusSearch()}
          aria-label="Show search"
        >
          <SVGIcon size="large">{searchIcon}</SVGIcon>
        </IconButton>
      </div>
    </>
  );
};

const DesktopActions = () => {
  const [postId] = useSlug('postSlug');
  const [topicId] = useSlug('topicSlug');
  const { versionFrom } = useParams();

  const dispatch = useAppDispatch();
  const location = useLocation();

  const isEditorReady = useAppSelector((state) => !!state.editor.postId);
  const isAdmin =
    useAppSelector((state) => state.session.user).type === 'ADMIN';
  const planTier = useAppSelector(
    (state) => state.session.organization.planTier,
  );

  const { data: postData } = useQuery(POST_QUERY, {
    variables: { id: postId || '' },
    skip: !postId,
  });

  const post = postData?.post;

  return (
    <div className={styles.actions}>
      {isAdmin && planTier === PlanTier.Free && location.pathname === '/' && (
        <div className={styles.upgradeText}>
          <span className={styles.upgradeTextInfo}>
            Collaborate with unlimited people.{' '}
          </span>
          <Button
            variant="link"
            onClick={() => openPricingModal(dispatch, 'users')}
          >
            Upgrade now
          </Button>
        </div>
      )}
      {!versionFrom &&
        (postId ? (
          isEditorReady &&
          post && (
            <>
              <div className={styles.statusContainer}>
                <PostStatus />
              </div>
              <div className={styles.presenceContainer}>
                <Presence />
              </div>
              {post.externalPost ? (
                <Button
                  variant="secondary"
                  to={post.externalPost.editUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {externalEditText(post.externalPost.platform)}
                </Button>
              ) : (
                <div className={styles.modeToggleContainer}>
                  <PostModeToggle />
                </div>
              )}
              <div className={styles.modeToggleSeparator} />
              <PostEditButton post={post} />
            </>
          )
        ) : (
          <TopbarCreatePostButton topicId={topicId} />
        ))}
    </div>
  );
};

const MobileActions = () => {
  const [postId] = useSlug('postSlug');
  const [topicId] = useSlug('topicSlug');
  const { versionFrom } = useParams();
  const isEditorReady = useAppSelector((state) => !!state.editor.postId);

  const { data: postData } = useQuery(POST_QUERY, {
    variables: { id: postId || '' },
    skip: !postId,
  });
  const post = postData?.post;

  const actionsClassName = classNames(styles.actions, styles.mobile);

  if (versionFrom) {
    return (
      <>
        <div className={actionsClassName} />
        <UserMenu />
      </>
    );
  }

  if (!postId) {
    return (
      <>
        <div className={actionsClassName}>
          <TopbarCreatePostButton topicId={topicId} />
        </div>
        <UserMenu />
      </>
    );
  }

  return (
    <div className={actionsClassName}>
      <PostModeToggle mobile />
      {isEditorReady && post && (
        <PostEditButton showSharePostMenuItem post={post} />
      )}
    </div>
  );
};

const UserMenu = () => (
  <div className={styles.userMenu}>
    <TopbarUserButton />
  </div>
);

const InternalTopbar = () => {
  const isMobileLayout = useIsLayoutSize(LayoutSize.mobile);

  if (isMobileLayout && isFeatureEnabled('new_post_layout')) {
    return (
      <Container>
        <SidebarToggle />
        <Search />
        <MobileActions />
      </Container>
    );
  }

  return (
    <Container>
      <SidebarToggle />
      <Search />
      <DesktopActions />
      <UserMenu />
    </Container>
  );
};

export default InternalTopbar;
